import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import './CreateCustomFields.scss';
import { useFormik } from 'formik';
import * as Yup from "yup";
import useCustomFields from '../../../Hooks/useCustomFields';
import Loading from '../../../components/LoadingPage/Loading';
import { useAppDispatch } from '../../../redux/hooks';
import { setCustomFieldsMetadata } from '../../../redux/customFields/customFieldsSlice';
import { useNavigate } from 'react-router-dom';

const CreateCustomFields = () => {
  const { getCustomFieldsControlTypeAPI,
    getCustomFieldsControlTypeLoading,
    getCustomFieldsDataTypeAPI,
    getCustomFieldsDataTypeLoading,
    getCustomFieldsModuleAPI,
    getCustomFieldsModuleLoading,
    createCustomFieldsMetadataAPI,
    createCustomFieldsMetadataLoading,
    getCustomFieldsMetadataAPI
  } = useCustomFields();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [controlTypeOptions, setControlTypeOptions] = useState<any>([]);
  const [dataTypeOptions, setDataTypeOptions] = useState<any>([]);
  const [moduleOptions, setModuleOptions] = useState<any>([]);
  const [selectedControlType, setSelectedControlType] = useState<string>("");

  const { handleChange, values, handleSubmit, touched, errors, setFieldValue } = useFormik({
    initialValues: {
      name: "",
      controlType: "",
      dataType: "",
      defaultValue: "",
      module: "",
      isRequired: false,
      isActive: true,
      allowedValues: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter name"),
      controlType: Yup.string().required("Please select control type"),
      dataType: Yup.string().required("Please select data type"),
      defaultValue: Yup.string().test(
        "is-required-based-on-control-type",
        "Please enter default value",
        function (value) {
          const { selectedControlType } = this.parent;
          const isRequired =
            selectedControlType === "Dropdown" ||
            selectedControlType === "Radio Button";
          return !isRequired || !!value;
        }
      ),
      module: Yup.string().required("Please select module"),
      isRequired: Yup.boolean(),
      isActive: Yup.boolean()
    }),
    onSubmit: async (values: any) => {
      const result = values?.allowedValues?.split(",").map((option: any) => ({
        code: option.trim(),
        text: option.trim()
      }));

      await createCustomFieldsMetadataAPI({
        code: values.name.toLowerCase(),
        name: values.name,
        controlTypeId: values.controlType,
        dataTypeId: values.dataType,
        defaultValue: values.defaultValue,
        moduleId: values.module,
        allowedValues: JSON.stringify(result, null, 2),
        isActive: values.isActive,
        isRequired: values.isRequired,
      }).then(async (res: any) => {
        await getCustomFieldsMetadataAPI({}).then((resCustomMetadata) => {
          dispatch(setCustomFieldsMetadata(resCustomMetadata));
          navigate('/custom-fields-master');
        });
      }).catch((err: any) => {
        console.error(err);
      });
    },
  });

  const getControlType = async () => {
    try {
      const res = await getCustomFieldsControlTypeAPI({});
      setControlTypeOptions(
        res.map((item: any) => ({
          value: item?.id,
          label: item?.name
        }))
      );
    } catch (error) {
      console.error('Failed to fetch control types:', error);
    }
  };
  const getDataType = async () => {
    try {
      const res = await getCustomFieldsDataTypeAPI({});
      setDataTypeOptions(
        res.map((item: any) => ({
          value: item?.id,
          label: item?.name
        }))
      );
    } catch (error) {
      console.error('Failed to fetch data types:', error);
    }
  };
  const getModules = async () => {
    try {
      const res = await getCustomFieldsModuleAPI({});
      setModuleOptions(
        res.map((item: any) => ({
          value: item?.id,
          label: item?.name
        }))
      );
    } catch (error) {
      console.error('Failed to fetch modules:', error);
    }
  };
  useEffect(() => {
    getControlType();
    getDataType();
    getModules();
  }, [])
  useEffect(() => {
    const selectedControlTypeLabel = controlTypeOptions.find(
      (opt: any) => opt.value === values.controlType
    )?.label;
    setSelectedControlType(selectedControlTypeLabel || '');
  }, [controlTypeOptions, values.controlType]);

  return (
    <div className='create-custom-fields-wrapper'>
      {
        getCustomFieldsControlTypeLoading ||
          getCustomFieldsDataTypeLoading ||
          getCustomFieldsModuleLoading ||
          createCustomFieldsMetadataLoading
          ? <Loading /> : null
      }
      <div className='page-title'>
        <h2>Create Custom Fields</h2>
      </div>
      <div className='create-custom-fields-box'>
        <Form onSubmit={handleSubmit}>
          <Form.Group className='mb-4'>
            <Form.Label>
              Name<sup>*</sup>
            </Form.Label>
            <Form.Control
              type='text'
              name='name'
              placeholder='Enter name'
              onChange={handleChange}
              value={values.name}
              isInvalid={touched.name && !!errors.name}
            />
            {touched.name && typeof errors.name === 'string' && (
              <Form.Control.Feedback type='invalid'>
                {errors.name}
              </Form.Control.Feedback>
            )}

          </Form.Group>

          <Form.Group className='mb-4'>
            <Form.Label>
              Control Type<sup>*</sup>
            </Form.Label>
            <Select
              name='controlType'
              options={controlTypeOptions}
              value={controlTypeOptions.find((opt: any) => opt.value === values.controlType)}
              onChange={(selected) =>
                setFieldValue('controlType', selected ? selected.value : '')
              }
              classNamePrefix='react-select'
            />
            {touched.controlType && typeof errors.controlType === 'string' && (
              <Form.Control.Feedback type='invalid'>
                {errors.controlType}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          {selectedControlType === 'Dropdown' ||
            selectedControlType === 'Radio Button' ? (
            <Form.Group className='mb-4'>
              <Form.Label>
                Allowed Values<sup>*</sup> (comma-separated)
              </Form.Label>
              <Form.Control
                type='text'
                name='allowedValues'
                placeholder='e.g. Option1, Option2'
                onChange={handleChange}
                value={values.allowedValues}
                isInvalid={touched.allowedValues && !!errors.allowedValues}
              />
              {touched.allowedValues && typeof errors.allowedValues === 'string' && (
                <Form.Control.Feedback type='invalid'>
                  {errors.allowedValues}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          ) : null}

          <Form.Group className='mb-4'>
            <Form.Label>
              Data Type<sup>*</sup>
            </Form.Label>
            <Select
              name='dataType'
              options={dataTypeOptions}
              value={dataTypeOptions.find((opt: any) => opt.value === values.dataType)}
              onChange={(selected) =>
                setFieldValue('dataType', selected ? selected.value : '')
              }
              classNamePrefix='react-select'
            />
            {touched.dataType && typeof errors.dataType === 'string' && (
              <Form.Control.Feedback type='invalid'>
                {errors.dataType}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          {
            selectedControlType === 'Dropdown' ||
              selectedControlType === 'Radio Button' ? (
              <Form.Group className='mb-4'>
                <Form.Label>
                  Default Value<sup>*</sup>
                </Form.Label>
                <Form.Control
                  type='text'
                  name='defaultValue'
                  placeholder='Enter Default Value'
                  onChange={handleChange}
                  value={values.defaultValue}
                  isInvalid={touched.defaultValue && !!errors.defaultValue}
                />
                {touched.defaultValue && typeof errors.defaultValue === 'string' && (
                  <Form.Control.Feedback type='invalid'>
                    {errors.defaultValue}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            ) : null
          }


          <Form.Group className='mb-4'>
            <Form.Label>
              Module<sup>*</sup>
            </Form.Label>
            <Select
              name='module'
              options={moduleOptions}
              value={moduleOptions.find((opt: any) => opt.value === values.module)}
              onChange={(selected) =>
                setFieldValue('module', selected ? selected.value : '')
              }
              classNamePrefix='react-select'
            />
            {touched.module && typeof errors.module === 'string' && (
              <Form.Control.Feedback type='invalid'>
                {errors.module}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className='mb-4'>
            <div className='item-checkbox-content'>
              <Form.Check
                inline
                label='Required'
                type='checkbox'
                name='isRequired'
                checked={values.isRequired}
                onChange={handleChange}
              />
              <Form.Check
                inline
                label='Active'
                type='checkbox'
                name='isActive'
                checked={values.isActive}
                onChange={handleChange}
              />
            </div>
          </Form.Group>

          <Form.Group className='mb-4 full-width text-end'>
            <Button variant='outline-primary' style={{ marginRight: '10px' }} onClick={() => navigate('/custom-fields-master')}>
              Cancel
            </Button>
            <Button type='submit'>Create</Button>
          </Form.Group>
        </Form>
      </div>
    </div>
  );
}

export default CreateCustomFields;
